import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Calendar, 
  Save, 
  AlertCircle, 
  CheckCircle,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';
import { RootState } from '../store';
import { clientAuthApi } from '../services/clientAuthApi';
import AccountLayout from '../components/AccountLayout';
import { ClientUser } from '@digitaltattoo/types';
import { updateUser } from '../store/slices/authSlice';
import {
  formatPhoneAsYouType,
  isMinor as computeIsMinor,
  parseInternationalPhone,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput, type InternationalPhoneChangeResult } from '../components/InternationalPhoneInput';

interface LegalGuardianFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationshipToMinor: 'parent' | 'legal_guardian' | 'other';
  relationshipDetails?: string;
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  isMinor: boolean | null;
  legalGuardian: LegalGuardianFormData | null;
}

const getPhoneState = (
  value: string,
  fallbackCountry: SupportedPhoneCountry = 'FR',
): InternationalPhoneChangeResult => {
  const parsed = parseInternationalPhone(value, fallbackCountry);
  const country = parsed.country ?? fallbackCountry;

  return {
    displayValue: formatPhoneAsYouType(value, country),
    e164Value: parsed.isValid ? parsed.e164 : null,
    country,
    isValid: parsed.isValid,
  };
};

export function ProfilePage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null,
    legalGuardian: null
  });

  const clientIsMinor = formData.dateOfBirth
    ? computeIsMinor(formData.dateOfBirth)
    : formData.isMinor;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [phoneState, setPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  const [guardianPhoneState, setGuardianPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));

  // Redirect if not authenticated (reacts to auth changes)
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Load profile data once on mount (not on every `user` change to avoid infinite loop)
  useEffect(() => {
    if (isAuthenticated) {
      loadUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      // Fetch fresh profile data from the API to get all fields (including dateOfBirth)
      const token = localStorage.getItem('client_token');
      let freshUser: ClientUser | null = null;
      if (token) {
        try {
          const result = await clientAuthApi.getCurrentUser(token);
          freshUser = result.user;
          // Update Redux store & localStorage with fresh data
          dispatch(updateUser(freshUser));
        } catch (apiErr) {
          console.warn('Failed to fetch fresh profile from API, falling back to local data:', apiErr);
        }
      }
      const src = freshUser || user;
      const srcIsMinor = src?.dateOfBirth ? computeIsMinor(src.dateOfBirth) : false;
      const nextPhoneState = getPhoneState(src?.phone || '');
      const nextGuardianPhoneState = getPhoneState(src?.legalGuardian?.phone || '');
      setPhoneState(nextPhoneState);
      setGuardianPhoneState(nextGuardianPhoneState);
      const emptyGuardian: LegalGuardianFormData = {
        firstName: '', lastName: '', email: '', phone: '',
        relationshipToMinor: 'parent', relationshipDetails: '',
      };
      setFormData({
        firstName: src?.firstName || '',
        lastName: src?.lastName || '',
        email: src?.email || '',
        phone: nextPhoneState.displayValue,
        dateOfBirth: src?.dateOfBirth || '',
        isMinor: typeof src?.isMinor === 'boolean' ? src.isMinor : null,
        legalGuardian: src?.legalGuardian ? {
          firstName: src.legalGuardian.firstName,
          lastName: src.legalGuardian.lastName,
          email: src.legalGuardian.email,
          phone: nextGuardianPhoneState.displayValue,
          relationshipToMinor: src.legalGuardian.relationshipToMinor,
          relationshipDetails: src.legalGuardian.relationshipDetails,
        } : srcIsMinor ? emptyGuardian : null
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError(t('auth.account.profile.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientIsMinor) {
      setFormData(prev => {
        if (prev.legalGuardian) return prev;
        return {
          ...prev,
          legalGuardian: {
            firstName: '', lastName: '', email: '', phone: '',
            relationshipToMinor: 'parent' as const, relationshipDetails: '',
          }
        };
      });
    }
  }, [clientIsMinor]);

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Clear success message
    if (success) {
      setSuccess('');
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = t('auth.account.profile.errors.firstNameRequired');
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('auth.account.profile.errors.emailInvalid');
    }

    if (phoneState.displayValue.trim() && (!phoneState.isValid || !phoneState.e164Value)) {
      errors.phone = t('auth.account.profile.errors.phoneInvalid');
    }

    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 13) {
        errors.dateOfBirth = t('auth.account.profile.errors.ageRequirement');
      }
    }

    if (clientIsMinor && formData.legalGuardian) {
      const g = formData.legalGuardian;
      if (g.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.email)) {
        errors['guardian.email'] = t('customerDetails.guest.errors.guardianEmailInvalid');
      }
      if (g.phone.trim() && (!guardianPhoneState.isValid || !guardianPhoneState.e164Value)) {
        errors['guardian.phone'] = t('auth.account.profile.errors.phoneInvalid');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('client_token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const result = await clientAuthApi.updateProfile(token, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: phoneState.e164Value ?? '',
        dateOfBirth: formData.dateOfBirth || undefined,
        isMinor: clientIsMinor,
        legalGuardian: clientIsMinor && formData.legalGuardian ? {
          firstName: formData.legalGuardian.firstName.trim(),
          lastName: formData.legalGuardian.lastName.trim(),
          email: formData.legalGuardian.email.trim(),
          phone: guardianPhoneState.e164Value ?? '',
          relationshipToMinor: formData.legalGuardian.relationshipToMinor,
          relationshipDetails: formData.legalGuardian.relationshipDetails,
        } : undefined,
      });

      // Update Redux store with fresh user data from the API response
      dispatch(updateUser(result.user));

      setSuccess(t('auth.account.profile.success.updated'));
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = error instanceof Error ? error.message : t('auth.account.profile.errors.updateFailed');
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AccountLayout title={t('auth.account.profile.title')}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-textSecondary">{t('auth.account.profile.loading')}</p>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout 
      title={t('auth.account.profile.title')}
      subtitle={t('auth.account.profile.subtitle')}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow-sm rounded-lg border">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Personal Information Section */}
            <div>
              <h3 className="text-lg font-medium text-textPrimary mb-4">
                {t('auth.account.profile.sections.personalInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.profile.fields.firstName')} *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-textSecondary" />
                    </div>
                    <input
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        validationErrors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                      placeholder={t('auth.account.profile.fields.firstName')}
                    />
                  </div>
                  {validationErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validationErrors.firstName}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.profile.fields.lastName')} (optionnel)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-textSecondary" />
                    </div>
                    <input
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        validationErrors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                      placeholder={t('auth.account.profile.fields.lastName')}
                    />
                  </div>
                  {validationErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validationErrors.lastName}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div>
              <h3 className="text-lg font-medium text-textPrimary mb-4">
                {t('auth.account.profile.sections.contactInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.profile.fields.email')} (optionnel)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-textSecondary" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        validationErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                      placeholder={t('auth.account.profile.fields.email')}
                    />
                  </div>
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validationErrors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.profile.fields.phone')} (optionnel)
                  </label>
                  <InternationalPhoneInput
                    id="phone"
                    value={phoneState.displayValue}
                    country={phoneState.country}
                    onChange={(result) => {
                      setPhoneState(result);
                      setFormData((prev) => ({ ...prev, phone: result.displayValue }));
                      if (result.isValid && validationErrors.phone) {
                        setValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next.phone;
                          return next;
                        });
                      }
                    }}
                    selectClassName="block w-full px-3 py-3 border rounded-lg border-gray-300 bg-white"
                    inputClassName={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      validationErrors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                    placeholder={t('auth.account.profile.fields.phone')}
                    error={validationErrors.phone}
                  />
                </div>
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <h3 className="text-lg font-medium text-textPrimary mb-4">
                {t('auth.account.profile.sections.dateOfBirth')}
              </h3>
              
              <div className="max-w-md">
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.account.profile.fields.dateOfBirth')} (optionnel)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    disabled={formData.isMinor === true && !formData.dateOfBirth}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        dateOfBirth: value,
                        isMinor: value ? computeIsMinor(value) : null,
                      }));
                    }}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      validationErrors.dateOfBirth ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                  />
                </div>
                {validationErrors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.dateOfBirth}
                  </p>
                )}
                <p className="mt-2 text-sm text-textSecondary">
                  {t('auth.account.profile.fields.dateOfBirthHelp')}
                </p>
                <label className="mt-3 flex items-center gap-2 text-sm text-textPrimary">
                  <input
                    type="checkbox"
                    checked={clientIsMinor}
                    disabled={Boolean(formData.dateOfBirth)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isMinor: e.target.checked ? true : null,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  Mineur (optionnel)
                </label>
              </div>
            </div>

            {/* Legal Guardian Section (visible when minor) */}
            {clientIsMinor && formData.legalGuardian && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 text-amber-600" />
                  <h3 className="text-lg font-medium text-textPrimary">
                    {t('customerDetails.guest.legalGuardian', 'Legal Guardian Information')}
                  </h3>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-amber-800">
                    {t('customerDetails.piercingMinorWarning.description', 'As you are under 18, your legal guardian must give their written consent and be physically present during the piercing appointment.')}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('customerDetails.guest.guardianFirstName', 'Guardian First Name')}
                    </label>
                    <input
                      type="text"
                      value={formData.legalGuardian.firstName}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, firstName: e.target.value } : null
                      }))}
                      className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        validationErrors['guardian.firstName'] ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    />
                    {validationErrors['guardian.firstName'] && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {validationErrors['guardian.firstName']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('customerDetails.guest.guardianLastName', 'Guardian Last Name')}
                    </label>
                    <input
                      type="text"
                      value={formData.legalGuardian.lastName}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, lastName: e.target.value } : null
                      }))}
                      className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        validationErrors['guardian.lastName'] ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    />
                    {validationErrors['guardian.lastName'] && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {validationErrors['guardian.lastName']}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('customerDetails.guest.guardianEmail', 'Guardian Email')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-textSecondary" />
                      </div>
                      <input
                        type="email"
                        value={formData.legalGuardian.email}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, email: e.target.value } : null
                        }))}
                        className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                          validationErrors['guardian.email'] ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                        }`}
                      />
                    </div>
                    {validationErrors['guardian.email'] && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {validationErrors['guardian.email']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('customerDetails.guest.guardianPhone', 'Guardian Phone')}
                    </label>
                    <InternationalPhoneInput
                      required
                      value={guardianPhoneState.displayValue}
                      country={guardianPhoneState.country}
                      onChange={(result) => {
                        setGuardianPhoneState(result);
                        setFormData((prev) => ({
                          ...prev,
                          legalGuardian: prev.legalGuardian
                            ? { ...prev.legalGuardian, phone: result.displayValue }
                            : null,
                        }));
                        if (result.isValid && validationErrors['guardian.phone']) {
                          setValidationErrors((prev) => {
                            const next = { ...prev };
                            delete next['guardian.phone'];
                            return next;
                          });
                        }
                      }}
                      selectClassName="block w-full px-3 py-3 border rounded-lg border-gray-300 bg-white"
                      inputClassName={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        validationErrors['guardian.phone'] ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                      error={validationErrors['guardian.phone']}
                    />
                  </div>
                </div>

                <div className="mt-4 max-w-md">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('customerDetails.guest.relationship', 'Relationship')}
                  </label>
                  <select
                    value={formData.legalGuardian.relationshipToMinor}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      legalGuardian: prev.legalGuardian ? { ...prev.legalGuardian, relationshipToMinor: e.target.value as any } : null
                    }))}
                    className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white"
                  >
                    <option value="parent">{t('customerDetails.guest.relationshipOptions.parent', 'Parent')}</option>
                    <option value="legal_guardian">{t('customerDetails.guest.relationshipOptions.legalGuardian', 'Legal Guardian')}</option>
                    <option value="other">{t('customerDetails.guest.relationshipOptions.other', 'Other')}</option>
                  </select>
                </div>
              </div>
            )}

            {/* Success/Error Messages */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <div className="ml-3">
                    <p className="text-sm text-green-800">{success}</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('auth.account.profile.saving')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {t('auth.account.profile.saveChanges')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AccountLayout>
  );
}

export default ProfilePage;
